<template>
  <div class="w1200 zhaogu-page">
    <div class="page-bar">
      <span class="back-btn" @click="goBack">← 返回</span>
    </div>
    <div class="page-title">众筹招股详情</div>

    <div v-loading="loading" style="min-height: 200px;">
      <template v-if="detail">
        <!-- 基础信息 -->
        <div class="info-card">
          <div class="info-row">
            <div class="info-item"><span class="label">众筹标题</span><span class="value">{{ detail.title }}</span></div>
            <div class="info-item"><span class="label">期号</span><span class="value">{{ detail.issueNumber }}</span></div>
            <div class="info-item"><span class="label">彩种</span><span class="value">{{ lotteryTypeName(detail.lotteryType) }}</span></div>
          </div>
          <div class="info-row">
            <div class="info-item"><span class="label">投注站号</span><span class="value">{{ detail.bettingShopNumber || '-' }}</span></div>
            <div class="info-item"><span class="label">投注站点</span><span class="value">{{ detail.bettingShopAddress || '-' }}</span></div>
            <div class="info-item"><span class="label">关联实战方案</span><span class="value">{{ detail.schemeName || '未关联' }}</span></div>
          </div>
          <div class="info-row">
            <div class="info-item"><span class="label">众筹总额</span><span class="value">{{ money(detail.crowdfundingAmount) }} 元</span></div>
            <div class="info-item"><span class="label">每股价格</span><span class="value">{{ money(detail.crowdfundingPrice) }} 缩币</span></div>
            <div class="info-item"><span class="label">总股数</span><span class="value">{{ detail.totalShareNumber }} 股</span></div>
          </div>
          <div class="info-row">
            <div class="info-item"><span class="label">已认购</span><span class="value">{{ detail.subscribedShareNumber }} 股</span></div>
            <div class="info-item"><span class="label">剩余可认购</span><span class="value">{{ detail.remainingShareNumber }} 股</span></div>
            <div class="info-item progress-item">
              <span class="label">认购进度</span>
              <span class="value">
                <el-progress :percentage="progressPercent" :stroke-width="10" style="width: 160px;"></el-progress>
              </span>
            </div>
          </div>
        </div>

        <!-- 实战票盈亏（开奖后才有数据） -->
        <div class="block" v-if="isDrawn">
          <h4 class="block-title">实战票盈亏与分红</h4>
          <div class="info-card">
            <div class="info-row">
              <div class="info-item"><span class="label">税前中奖总额</span><span class="value">{{ money(detail.beforeTaxAmount) }} 元</span></div>
              <div class="info-item"><span class="label">税后中奖总额</span><span class="value">{{ money(detail.afterTaxAmount) }} 元</span></div>
              <div class="info-item"><span class="label">实战票盈亏总额</span><span class="value" :class="profitClass">{{ money(detail.profitLossAmount) }} 元</span></div>
            </div>
            <div class="info-row">
              <div class="info-item"><span class="label">每股分红</span><span class="value">{{ money(detail.distributeBonus) }} 缩币</span></div>
              <div class="info-item"><span class="label">应分红总额</span><span class="value">{{ money(detail.totalPayableAmount) }} 缩币</span></div>
              <div class="info-item"></div>
            </div>
            <div class="formula" v-if="detail.profitLossFormula">{{ detail.profitLossFormula }}</div>
            <div class="formula" v-if="detail.dividendFormula">{{ detail.dividendFormula }}</div>
          </div>
        </div>
        <div class="block" v-else>
          <h4 class="block-title">实战票盈亏与分红</h4>
          <div class="tip-msg">本期实战票尚未开奖，开奖并派奖后展示盈亏与每股分红。</div>
        </div>

        <!-- 操作按钮区 -->
        <div class="op-bar">
          <el-button type="primary" :disabled="!canSubscribe" @click="openSubscribe">
            {{ canSubscribe ? (mySubscribe ? '续购股权' : '认购股权') : (mySubscribe ? '已认购' : '暂不可认购') }}
          </el-button>
          <el-button @click="goMySubscribe">我的认购</el-button>
          <el-button @click="goMyOffering">我发布的招股</el-button>
          <el-button type="primary" plain @click="openEquity">股权与分红清单</el-button>
          <el-button v-if="mySubscribe" type="warning" plain @click="openContract">查看四件套披露</el-button>
          <el-button v-if="detail.qrcodeUrl" @click="qrVisible = true">分享二维码</el-button>
        </div>

        <!-- 我的认购 -->
        <div class="block" v-if="mySubscribe">
          <h4 class="block-title">我的认购</h4>
          <div class="info-card">
            <div class="info-row">
              <div class="info-item"><span class="label">认购股数</span><span class="value">{{ mySubscribe.purchaserNumber }} 股</span></div>
              <div class="info-item"><span class="label">每股金额</span><span class="value">{{ money(mySubscribe.payablePrice) }} 缩币</span></div>
              <div class="info-item"><span class="label">应得分红</span><span class="value">{{ money(mySubscribe.payable) }} 缩币</span></div>
            </div>
            <div class="info-row">
              <div class="info-item"><span class="label">派奖状态</span><span class="value">{{ mySubscribe.bonusStatusIntro || '-' }}</span></div>
              <div class="info-item"><span class="label">认购时间</span><span class="value">{{ mySubscribe.createTime || '-' }}</span></div>
              <div class="info-item"></div>
            </div>
          </div>
        </div>

        <!-- 认购人清单 -->
        <div class="block">
          <h4 class="block-title purchaser-title">
            <span>认购人清单（谁买了 / 各买多少股 / 分红 / 派奖）</span>
            <el-radio-group v-model="purchaserSort" size="mini">
              <el-radio-button label="desc">最新在前</el-radio-button>
              <el-radio-button label="asc">最早在前</el-radio-button>
            </el-radio-group>
          </h4>
          <el-table :data="sortedPurchaserList" empty-text="暂无认购记录"
            :row-class-name="purchaserRowClass"
            :header-cell-style="{ 'textAlign': 'center' }" :cell-style="{ 'textAlign': 'center' }">
            <el-table-column type="index" label="序号" width="60"></el-table-column>
            <el-table-column prop="purchaser" label="认购人" show-overflow-tooltip></el-table-column>
            <el-table-column prop="purchaserNumber" label="认购股数"></el-table-column>
            <el-table-column label="持股比例">
              <template slot-scope="{ row }">{{ row.shareRatio != null ? row.shareRatio + '%' : '-' }}</template>
            </el-table-column>
            <el-table-column label="消耗缩币">
              <template slot-scope="{ row }">{{ money(row.consumeCoin) }}</template>
            </el-table-column>
            <el-table-column label="分红金额">
              <template slot-scope="{ row }">{{ money(row.dividendAmount) }}</template>
            </el-table-column>
            <el-table-column prop="bonusStatusIntro" label="派奖状态" width="130">
              <template slot-scope="{ row }">
                <el-tag v-if="row.bonusStatus === '2'" size="mini" effect="plain">内部认购，免打款</el-tag>
                <el-tag v-else-if="row.bonusStatus === '1'" size="mini" type="success" effect="plain">已派奖</el-tag>
                <el-tag v-else size="mini" type="info" effect="plain">待派奖</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </template>

      <div v-else-if="!loading" class="tip-msg">未获取到众筹详情，请返回重试。</div>
    </div>

    <!-- 认购对话框 -->
    <el-dialog title="认购股权" :visible.sync="subscribeDialog" width="540px" @close="onSubscribeClose" custom-class="subscribe-dialog" :show-close="true" :close-on-click-modal="false">
      <div v-if="detail" class="subscribe-card">
        <div class="sub-header">
          <div class="sub-avatar">
            <img v-if="vuex_userInfo.avatar" :src="$img(vuex_userInfo.avatar)" alt="头像" />
            <img v-else src="@/assets/default_avatar.png" alt="头像" />
          </div>
          <div class="sub-header-right">
            <div class="sub-user">{{ vuex_userInfo.nickName || '彩民用户' }}</div>
            <div class="sub-tip">特别提醒：散户可认购2股；缩水师、技巧师、软件师每具备一种身份可多认购2股，单人认购上限8股。您当前最多可认购 <b>{{ effectivePersonLimit || '-' }}</b> 股<template v-if="mySubscribe">，已认购 {{ mySubscribe.purchaserNumber }} 股</template>。</div>
          </div>
        </div>
        <div class="sub-info-row">
          <div class="sub-info-item">
            <span class="sub-info-label">投注站</span>
            <el-tooltip :content="shopTitle" placement="top" :disabled="!shopTitle || shopTitle === '-'" popper-class="sub-info-tooltip">
              <span class="sub-info-value">{{ shopTitle }}</span>
            </el-tooltip>
          </div>
          <div class="sub-info-item">
            <span class="sub-info-label">期号</span>
            <span class="sub-info-value">{{ detail.issueNumber }}</span>
          </div>
          <div class="sub-info-item">
            <span class="sub-info-label">每股价格</span>
            <span class="sub-info-value">{{ money(detail.crowdfundingPrice) }}缩币</span>
          </div>
          <div class="sub-info-item sub-input-item">
            <span class="sub-info-label">我要入</span>
            <el-input-number v-model="subscribeForm.purchaserNumber" :min="1" :max="maxSubscribeNumber" size="small" controls-position="right" @change="onNumberChange"></el-input-number>
            <span class="sub-info-label end-label">股</span>
          </div>
        </div>
        <div class="sub-pay-box">
          <div class="sub-pay-amount">{{ payableCoin }}缩币</div>
          <div class="sub-pay-label">应付缩币</div>
        </div>
        <div class="sub-agreement">
          <el-checkbox v-model="agreement">同意</el-checkbox>
          <span class="sub-agreement-text">《缩币网用户协议》</span>
        </div>
        <div class="sub-follow">
          <el-checkbox v-model="followThisTime">跟注该投注站</el-checkbox>
          <span class="sub-follow-text">
            本站新发招股自动按 <b>{{ subscribeForm.purchaserNumber || 1 }}</b> 股认购<template v-if="followSetting && followSetting.enabled === 1">（已开启，每期 {{ followSetting.shares }} 股）</template>，仅限本站；余额/上限/份额不足时当期跳过并记录。
          </span>
        </div>
        <div class="sub-pay-btn-wrap">
          <el-button type="primary" class="sub-pay-btn" :loading="subscribing" @click="submitSubscribe">立即支付 {{ payableCoin }}缩币</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 分享二维码 -->
    <el-dialog title="众筹分享二维码" :visible.sync="qrVisible" width="320px">
      <div class="qr-box" v-if="detail && detail.qrcodeUrl">
        <img :src="detail.qrcodeUrl" class="qr-big" alt="分享二维码" />
        <div class="qr-tip" v-if="detail.shareUrl">分享链接：<span class="link" @click="copyShare">{{ detail.shareUrl }}</span>（点击复制）</div>
      </div>
    </el-dialog>

    <!-- 四件套披露 -->
    <el-dialog title="帮扶四件套披露" :visible.sync="contractDialog" width="820px" @close="contractData = null">
      <div v-loading="contractLoading" style="min-height: 160px;">
        <template v-if="contractData">
          <div class="info-card">
            <div class="info-row">
              <div class="info-item"><span class="label">用户昵称</span><span class="value">{{ contractData.nickname || '-' }}</span></div>
              <div class="info-item"><span class="label">手机号</span><span class="value">{{ contractData.phoneNumber || '-' }}</span></div>
              <div class="info-item"><span class="label">真实姓名</span><span class="value">{{ contractData.identityCardName || '-' }}</span></div>
            </div>
            <div class="info-row">
              <div class="info-item"><span class="label">身份证号</span><span class="value">{{ contractData.identityCardNo || '-' }}</span></div>
              <div class="info-item"><span class="label">投注站地址</span><span class="value">{{ contractData.bettingShop || '-' }}</span></div>
              <div class="info-item"><span class="label">投注站编号</span><span class="value">{{ contractData.bettingShopNumber || '-' }}</span></div>
            </div>
          </div>
          <div class="contract-files">
            <div class="file-item">
              <div class="file-label">帮扶实战方案</div>
              <div v-if="contractData.practicalSchemes" class="file-value">
                <a :href="contractData.practicalSchemes" target="_blank" download>点击下载方案文件</a>
              </div>
              <div v-else class="file-empty">未上传</div>
            </div>
            <div class="file-item">
              <div class="file-label">实战票</div>
              <el-image v-if="contractData.practicalTicket" :src="contractData.practicalTicket" :preview-src-list="[contractData.practicalTicket]" style="width: 160px; height: 120px;" fit="cover"></el-image>
              <div v-else class="file-empty">未上传</div>
            </div>
            <div class="file-item">
              <div class="file-label">承诺签名后拍照</div>
              <el-image v-if="contractData.signature" :src="contractData.signature" :preview-src-list="[contractData.signature]" style="width: 160px; height: 120px;" fit="cover"></el-image>
              <div v-else class="file-empty">未上传</div>
            </div>
            <div class="file-item">
              <div class="file-label">签名后合同拍照</div>
              <el-image v-if="contractData.contractSignature" :src="contractData.contractSignature" :preview-src-list="[contractData.contractSignature]" style="width: 160px; height: 120px;" fit="cover"></el-image>
              <div v-else class="file-empty">未上传</div>
            </div>
          </div>
        </template>
        <div v-else-if="!contractLoading" class="tip-msg">投注站尚未上传四件套或未认购该招股</div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  bettingShopOfferingDetail,
  bettingShopSubscribe,
  bettingShopContractOffering,
  crowdfundingFollowSave,
  crowdfundingFollowSetting,
} from "@/api/apply";

const LOTTERY_MAP = {
  '0': '双色球', '1': '大乐透', '2': '福彩3D', '3': '排列3',
  '5': '七乐彩', '6': '7星彩', '7': '排列5', '8': '快乐8',
};

export default {
  name: "ZhaoguDetail",
  data() {
    return {
      id: null,
      loading: false,
      detail: null,
      mySubscribe: null,
      purchaserList: [],
      // 认购人清单排序：desc=最新认购在前（默认，与后端返回一致），asc=最早在前
      purchaserSort: "desc",
      subscribeDialog: false,
      subscribing: false,
      subscribeForm: { purchaserNumber: 1 },
      agreement: false,
      // 本次认购时是否顺带开启自动跟注
      followThisTime: false,
      // 当前用户的跟注设置（enabled=1 已开启）
      followSetting: null,
      qrVisible: false,
      // 四件套披露
      contractDialog: false,
      contractData: null,
      contractLoading: false,
    };
  },
  computed: {
    // 认购人清单按选择排序展示（后端默认按认购记录 id 倒序返回，desc 即原序）
    sortedPurchaserList() {
      if (this.purchaserSort === "asc") {
        return (this.purchaserList || []).slice().reverse();
      }
      return this.purchaserList || [];
    },
    isDrawn() {
      return !!(this.detail && this.detail.afterTaxAmount != null);
    },
    shopTitle() {
      const addr = this.detail && this.detail.bettingShopAddress ? this.detail.bettingShopAddress : ''
      const no = this.detail && this.detail.bettingShopNumber ? this.detail.bettingShopNumber : ''
      if (addr && no) return addr + '（' + no + '）'
      return addr || no || '-'
    },
    effectivePersonLimit() {
      if (!this.detail) return 0;
      const total = Number(this.detail.totalShareNumber || 0);
      // 优先用后端按"身份加成"算好的单人上限（散户2股+三类身份各+2股，封顶8股，已与发布方每人上限取小）
      const mine = Number(this.detail.mySubscribeLimit || 0);
      if (mine > 0) return Math.min(mine, total || mine);
      let limit = Number(this.detail.personLimit || 0);
      if (!limit || limit <= 0) {
        limit = Math.min(total, 8);
      }
      // 后端未回填身份上限时，按"单人上限封顶8股"兜底
      let result = Math.max(Math.min(limit, 8), 0);
      // 旧版后端 personLimit/totalShareNumber 都没回时，按业务封顶 8 股兜底，避免弹框股数完全不可选
      if (result <= 0) result = 8;
      return result;
    },
    personRemaining() {
      const already = this.mySubscribe ? Number(this.mySubscribe.purchaserNumber || 0) : 0;
      return Math.max(this.effectivePersonLimit - already, 0);
    },
    canSubscribe() {
      if (!this.detail) return false;
      const remaining = Number(this.detail.remainingShareNumber || 0);
      const progress = Number(this.detail.subscribeProgress || 0);
      return remaining > 0 && progress < 100 && this.personRemaining > 0;
    },
    progressPercent() {
      const p = Number(this.detail ? this.detail.subscribeProgress || 0 : 0);
      return p > 100 ? 100 : p;
    },
    maxSubscribeNumber() {
      const remaining = Number(this.detail ? this.detail.remainingShareNumber || 0 : 0);
      return Math.min(remaining, this.personRemaining);
    },
    payableCoin() {
      const price = Number(this.detail ? this.detail.crowdfundingPrice || 0 : 0);
      const num = Number(this.subscribeForm.purchaserNumber || 0);
      return this.multiply(price, num);
    },
    profitClass() {
      const v = Number(this.detail ? this.detail.profitLossAmount || 0 : 0);
      return v >= 0 ? "profit-up" : "profit-down";
    },
  },
  mounted() {
    this.id = this.$route.params.id;
    this.loadDetail();
  },
  methods: {
    // 平台内部认购行（平台/实战团队/投注站/栏目组）加特殊底色区分
    purchaserRowClass({ row }) {
      return row.bonusStatus === "2" ? "internal-purchaser-row" : "";
    },
    isLoggedIn() {
      return !!(this.$store && this.$store.state && this.$store.state.vuex_token);
    },
    goBack() {
      this.$router.push("/sttzzbfzx/zhaogu");
    },
    goMySubscribe() {
      this.$router.push("/sttzzbfzx/my/subscribe");
    },
    goMyOffering() {
      this.$router.push("/sttzzbfzx/my/offering");
    },
    loadDetail() {
      if (!this.id) return;
      this.loading = true;
      bettingShopOfferingDetail(this.id)
        .then((res) => {
          const data = res && res.data !== undefined ? res.data : res;
          if (data) {
            this.detail = data;
            this.mySubscribe = data.mySubscribe || null;
            this.purchaserList = data.purchaserList || [];
            // 未登录不查跟注设置，避免触发全局登录过期弹窗；
            // 必须等 detail 返回后才能拿到发布方 shopUserId（跟注是按投注站隔离的）
            if (this.isLoggedIn()) {
              this.loadFollowSetting();
            }
          } else {
            this.detail = null;
          }
          this.loading = false;
        })
        .catch(() => {
          this.detail = null;
          this.loading = false;
        });
    },
    lotteryTypeName(type) {
      return LOTTERY_MAP[type] || type || "-";
    },
    money(v) {
      if (v == null || v === "") return "-";
      const n = Number(v);
      return isNaN(n) ? v : n;
    },
    multiply(a, b) {
      const x = Number(a || 0);
      const y = Number(b || 0);
      return Math.round(x * y * 100) / 100;
    },
    onNumberChange() {
      const max = this.maxSubscribeNumber;
      if (this.subscribeForm.purchaserNumber > max) {
        this.subscribeForm.purchaserNumber = max;
      }
      if (this.subscribeForm.purchaserNumber < 1) {
        this.subscribeForm.purchaserNumber = 1;
      }
    },
    openSubscribe() {
      this.subscribeForm.purchaserNumber = 1;
      this.agreement = false;
      // 已开启跟注的用户默认带入勾选状态，避免重复设置时误关
      this.followThisTime = !!(this.followSetting && this.followSetting.enabled === 1);
      this.subscribeDialog = true;
    },
    onSubscribeClose() {
      this.subscribeForm.purchaserNumber = 1;
      this.agreement = false;
      this.followThisTime = false;
    },
    submitSubscribe() {
      const num = Number(this.subscribeForm.purchaserNumber || 0);
      if (!num || num < 1) {
        this.$message.warning("请填写认购股数");
        return;
      }
      if (num > this.maxSubscribeNumber) {
        this.$message.warning("认购股数超出可认购上限（受身份上限/发布方限购/剩余股数约束）");
        return;
      }
      if (!this.agreement) {
        this.$message.warning("请先同意用户协议");
        return;
      }
      this.subscribing = true;
      bettingShopSubscribe({ crowdfundingId: this.id, purchaserNumber: num })
        .then((res) => {
          if (res && res.code === 200) {
            this.$message.success("认购成功");
            // 顺带开启自动跟注：以本次认购股数作为每期跟注股数
            if (this.followThisTime) {
              this.saveFollowAfterSubscribe(num);
            }
            this.subscribeDialog = false;
            this.loadDetail();
          } else {
            this.$message.error((res && res.msg) || "认购失败");
          }
        })
        .catch(() => {})
        .finally(() => {
          this.subscribing = false;
        });
    },
    // 认购成功后顺带开启对该投注站的自动跟注；失败不回滚认购，仅提示
    saveFollowAfterSubscribe(shares) {
      const shopUserId = this.detail && this.detail.shopUserId;
      if (!shopUserId) {
        this.$message.warning("未能识别发布方投注站，请到「我的跟注」页手动开启");
        return;
      }
      const n = Math.min(Math.max(Number(shares) || 2, 1), 8);
      crowdfundingFollowSave({ shopUserId: Number(shopUserId), enabled: true, shares: n })
        .then((res) => {
          if (res && res.code === 200) {
            this.$message.success((res && res.msg) || ("已开启跟注，该投注站之后新发布的众筹将自动按 " + n + " 股认购"));
            this.loadFollowSetting();
          } else {
            this.$message.warning((res && res.msg) || "自动跟注开启失败，可在「我的跟注」页设置");
          }
        })
        .catch(() => {
          this.$message.warning("自动跟注开启失败，可在「我的跟注」页重新设置");
        });
    },
    loadFollowSetting() {
      const shopUserId = this.detail && this.detail.shopUserId;
      if (!shopUserId) return;
      crowdfundingFollowSetting(shopUserId)
        .then((res) => {
          const data = res && res.data !== undefined ? res.data : res;
          this.followSetting = data || null;
        })
        .catch(() => {
          this.followSetting = null;
        });
    },
    // 股权与分红清单：跳转独立页面
    openEquity() {
      this.$router.push("/sttzzbfzx/equity/" + this.id);
    },
    profitClassOf(v) {
      const n = Number(v || 0);
      return n >= 0 ? "profit-up" : "profit-down";
    },
    // 四件套披露：仅已认购散户可查看；用弹窗展示图片/文件
    openContract() {
      this.contractDialog = true;
      this.contractLoading = true;
      this.contractData = null;
      bettingShopContractOffering(this.id)
        .then((res) => {
          const data = res && res.data !== undefined ? res.data : res;
          this.contractData = data || null;
          this.contractLoading = false;
        })
        .catch(() => {
          this.contractData = null;
          this.contractLoading = false;
        });
    },
    copyShare() {
      const url = this.detail && this.detail.shareUrl;
      if (!url) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(() => this.$message.success("已复制分享链接"));
      } else {
        const input = document.createElement("input");
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
        this.$message.success("已复制分享链接");
      }
    },
    buildAbsUrl(relPath) {
      const base = (process.env.VUE_APP_BASE_API || "").replace(/\/+$/, "");
      const prefix = base + "/stage-api";
      return prefix + "/" + relPath.replace(/^\/+/, "");
    },
  },
};
</script>

<style lang="scss" scoped>
.zhaogu-page {
  background: #ffffff;
  padding: 20px;
  margin-top: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border-radius: 8px;

  .page-bar {
    margin-bottom: 10px;
  }

  .back-btn {
    display: inline-block;
    cursor: pointer;
    color: #347caf;
    font-size: 14px;
    padding: 6px 14px;
    border: 1px solid #347caf;
    border-radius: 5px;
    transition: all 0.2s;

    &:hover {
      background-color: #347caf;
      color: #ffffff;
    }
  }

  .page-title {
    font-size: 22px;
    font-weight: bold;
    color: #347caf;
    text-align: center;
    padding: 10px 0 20px;
    border-bottom: 2px solid #347caf;
    margin-bottom: 20px;
  }
}

.info-card {
  border: 1px solid #e4e7ed;
  border-radius: 10px;
  background: linear-gradient(135deg, #f5faff 0%, #eaf2fa 100%);
  padding: 18px 22px;

  .info-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px 40px;
    padding: 6px 0;

    .info-item {
      display: flex;
      align-items: center;
      min-width: 220px;

      .label {
        color: #347caf;
        font-size: 14px;
        font-weight: bold;
        margin-right: 10px;
        white-space: nowrap;
      }

      .value {
        color: #333;
        font-size: 14px;
      }

      &.progress-item .value {
        display: flex;
        align-items: center;
      }
    }
  }

  .formula {
    margin-top: 8px;
    color: #e67e22;
    font-size: 13px;
    line-height: 1.6;
  }
}

.block {
  margin-top: 18px;

  .block-title {
    color: #347caf;
    font-size: 16px;
    margin: 0 0 10px;
    padding-left: 10px;
    border-left: 4px solid #347caf;
  }

  .purchaser-title {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .el-radio-group {
      .el-radio-button__inner {
        padding: 4px 10px;
      }
    }
  }

  // 平台内部认购行：整行文字用主题蓝标识，与散户认购区分
  ::v-deep .internal-purchaser-row > td.el-table__cell {
    color: #347caf;
  }
}

.tip-msg {
  color: #898af7;
  font-weight: bold;
  padding: 14px 0;
  text-align: center;
}

.op-bar {
  margin: 18px 0 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.profit-up {
  color: #c0392b;
  font-weight: bold;
}

.profit-down {
  color: #27ae60;
  font-weight: bold;
}

.sub-form {
  .sub-line {
    display: flex;
    justify-content: space-between;
    padding: 6px 0;
    color: #333;

    &.total {
      margin-top: 6px;
      border-top: 1px dashed #e4e7ed;
      padding-top: 10px;
    }

    .red {
      color: #c0392b;
    }
  }
}

.subscribe-card {
  padding: 10px 6px;

  .sub-header {
    display: flex;
    align-items: flex-start;
    margin-bottom: 20px;

    .sub-avatar {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      overflow: hidden;
      flex-shrink: 0;
      background: #f0f0f0;
      margin-right: 14px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .sub-header-right {
      flex: 1;

      .sub-user {
        font-size: 16px;
        font-weight: bold;
        color: #333;
        margin-bottom: 6px;
      }

      .sub-tip {
        font-size: 12px;
        color: #e6a23c;
        line-height: 1.5;
        background: #fdf6ec;
        padding: 8px 10px;
        border-radius: 6px;
      }
    }
  }

  .sub-info-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    background: #f7f8fa;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 18px;

    .sub-info-item {
      display: flex;
      align-items: center;
      min-width: 140px;

      .sub-info-label {
        color: #666;
        font-size: 13px;
        margin-right: 8px;
        white-space: nowrap;

        &.end-label {
          margin-left: 6px;
          margin-right: 0;
        }
      }

      .sub-info-value {
        color: #333;
        font-size: 14px;
        font-weight: bold;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 180px;
      }
    }

    ::v-deep .el-input-number {
      width: 100px;

      .el-input__inner {
        text-align: center;
        padding-left: 10px;
        padding-right: 10px;
      }
    }
  }

  .sub-input-item {
    flex: 1;
    min-width: 180px;
  }

  .sub-pay-box {
    text-align: center;
    margin-bottom: 18px;

    .sub-pay-amount {
      display: inline-block;
      background: #f1c40f;
      color: #fff;
      font-size: 22px;
      font-weight: bold;
      padding: 12px 36px;
      border-radius: 30px;
      letter-spacing: 1px;
    }

    .sub-pay-label {
      font-size: 12px;
      color: #999;
      margin-top: 8px;
    }
  }

  .sub-agreement {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;

    .sub-agreement-text {
      color: #409eff;
      cursor: pointer;
      font-size: 13px;
      margin-left: 4px;
    }
  }

  .sub-follow {
    display: flex;
    align-items: flex-start;
    margin: -6px 0 14px;
    padding: 8px 10px;
    background: #f5f9fd;
    border: 1px dashed #c6ddf0;
    border-radius: 5px;

    .sub-follow-text {
      color: #7a8b99;
      font-size: 12px;
      line-height: 18px;
      margin-left: 4px;

      b {
        color: #347caf;
        padding: 0 2px;
      }
    }
  }

  .sub-pay-btn-wrap {
    text-align: center;

    .sub-pay-btn {
      width: 100%;
      height: 44px;
      font-size: 16px;
      border-radius: 22px;
    }
  }
}

.qr-box {
  text-align: center;

  .qr-big {
    width: 200px;
    height: 200px;
    object-fit: contain;
  }

  .qr-tip {
    margin-top: 10px;
    font-size: 12px;
    color: #888;

    .link {
      color: #347caf;
      cursor: pointer;
      word-break: break-all;
    }
  }
}

.contract-files {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 16px;

  .file-item {
    width: calc(50% - 10px);
    border: 1px dashed #c0c4cc;
    border-radius: 8px;
    padding: 14px;
    background: #fafafa;

    .file-label {
      font-size: 14px;
      font-weight: bold;
      color: #347caf;
      margin-bottom: 10px;
    }

    .file-value a {
      color: #409eff;
      text-decoration: none;
    }

    .file-empty {
      color: #909399;
      font-size: 13px;
    }
  }
}
</style>
